import { useEffect, useMemo, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import {
  createDocument,
  friendlyMessage,
  listDocTypes,
  listDocuments,
  listVersions,
} from "../../../controllers/eqmsApi";
import type { DocStatus, DocType, Document, Version, WorkAreaType } from "../../../models/eqms";
import { DOC_STATUS_LABEL, WORK_AREA_LABEL, WORK_AREAS } from "../../../models/eqms";

// 문서함: a project's (optionally work-area-scoped) controlled document register, with client-side
// status/work-area facet filters and a create-document form. Row click drills into the document detail.
export function EqmsDocumentsView({ eqms }: { eqms: EqmsController }) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [statusFilter, setStatusFilter] = useState<DocStatus | "ALL">("ALL");
  const [areaFilter, setAreaFilter] = useState<WorkAreaType | "ALL">("ALL");

  const { getToken, projectId, workArea } = eqms;

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listDocuments(getToken, projectId, workArea ?? undefined)
      .then((docs) => { if (!cancelled) setDocuments(docs); })
      .catch((e) => { if (!cancelled) setError(friendlyMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getToken, projectId, workArea]);

  async function reload() {
    if (!projectId) return;
    setError(null);
    try {
      setDocuments(await listDocuments(getToken, projectId, workArea ?? undefined));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (statusFilter !== "ALL" && d.status !== statusFilter) return false;
      if (!workArea && areaFilter !== "ALL" && d.workAreaType !== areaFilter) return false;
      return true;
    });
  }, [documents, statusFilter, areaFilter, workArea]);

  if (!projectId) return <div className="center">프로젝트를 선택하세요.</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 19 }}>
          문서함{workArea ? ` · ${WORK_AREA_LABEL[workArea]}` : ""}
        </h2>
        <button className="btn primary" style={{ width: "auto" }} onClick={() => setCreating((c) => !c)}>
          {creating ? "취소" : "＋ 새 문서"}
        </button>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {creating && (
        <CreateDocumentForm
          eqms={eqms}
          onDone={() => { setCreating(false); void reload(); }}
        />
      )}

      {/* Facets beside the list on a wide screen, stacked above it on a narrow one — see .facet-layout. */}
      <div className="facet-layout">
        <aside className="facet-rail">
          <div className="info-card">
            <h4>상태</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <FacetButton active={statusFilter === "ALL"} label={`전체 · ${documents.length}`} onClick={() => setStatusFilter("ALL")} />
              {(Object.keys(DOC_STATUS_LABEL) as DocStatus[]).map((s) => (
                <FacetButton
                  key={s}
                  active={statusFilter === s}
                  label={`${DOC_STATUS_LABEL[s]} · ${documents.filter((d) => d.status === s).length}`}
                  onClick={() => setStatusFilter(s)}
                />
              ))}
            </div>
          </div>
          {!workArea && (
            <div className="info-card" style={{ marginTop: 12 }}>
              <h4>영역</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <FacetButton active={areaFilter === "ALL"} label="전체" onClick={() => setAreaFilter("ALL")} />
                {WORK_AREAS.map((a) => (
                  <FacetButton key={a} active={areaFilter === a} label={WORK_AREA_LABEL[a]} onClick={() => setAreaFilter(a)} />
                ))}
              </div>
            </div>
          )}
        </aside>

        <div className="facet-content">
          {loading ? (
            <div className="center">불러오는 중…</div>
          ) : (
            <div className="sdui-table-wrap">
            <table className="sdui-table tbl-title2">
              <thead>
                <tr>
                  <th>문서번호</th><th>제목</th><th>상태</th><th>형식</th><th>수정일</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc) => (
                  <tr key={doc.id} style={{ cursor: "pointer" }} onClick={() => eqms.openDetail("documents", doc.id, doc.workAreaType)}>
                    <td className="mono">{doc.docNumber}</td>
                    <td>{doc.title}</td>
                    <td><span className="sdui-badge">{DOC_STATUS_LABEL[doc.status]}</span></td>
                    <td>{doc.format || "-"}</td>
                    <td>{new Date(doc.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              {filtered.length === 0 && (
                <tfoot><tr><td colSpan={5} className="sdui-table-empty">문서가 없습니다.</td></tr></tfoot>
              )}
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FacetButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      className="tag"
      style={{ textAlign: "left", border: active ? "1.5px solid var(--blue)" : "none", cursor: "pointer" }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function CreateDocumentForm({ eqms, onDone }: { eqms: EqmsController; onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [workAreaType, setWorkAreaType] = useState<WorkAreaType>(eqms.workArea ?? "SOFTWARE");
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [docTypeId, setDocTypeId] = useState<string>("");
  const [docNumber, setDocNumber] = useState("");
  const [versions, setVersions] = useState<Version[]>([]);
  const [targetVersionId, setTargetVersionId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eqms.workspaceId) return;
    listDocTypes(eqms.getToken, eqms.workspaceId).then(setDocTypes).catch(() => {});
  }, [eqms.getToken, eqms.workspaceId]);

  useEffect(() => {
    if (!eqms.projectId) return;
    listVersions(eqms.getToken, eqms.projectId).then(setVersions).catch(() => {});
  }, [eqms.getToken, eqms.projectId]);

  async function submit() {
    if (!eqms.projectId || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createDocument(eqms.getToken, eqms.projectId, {
        workAreaType,
        docTypeId: docTypeId || null,
        docNumber: docTypeId ? null : (docNumber.trim() || null),
        title: title.trim(),
        targetVersionId: targetVersionId || null,
      });
      onDone();
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card" style={{ marginBottom: 16 }}>
      <h4>새 문서</h4>
      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 12 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label className="sdui-field" style={{ flex: 2, minWidth: 220 }}>
          <span className="sdui-label">제목</span>
          <input className="sdui-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ minWidth: 140 }}>
          <span className="sdui-label">영역</span>
          <select className="sdui-input" value={workAreaType} onChange={(e) => setWorkAreaType(e.target.value as WorkAreaType)}>
            {WORK_AREAS.map((a) => <option key={a} value={a}>{WORK_AREA_LABEL[a]}</option>)}
          </select>
        </label>
        <label className="sdui-field" style={{ minWidth: 160 }}>
          <span className="sdui-label">문서 유형</span>
          <select className="sdui-input" value={docTypeId} onChange={(e) => setDocTypeId(e.target.value)}>
            <option value="">직접 번호 입력</option>
            {docTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        {!docTypeId && (
          <label className="sdui-field" style={{ minWidth: 140 }}>
            <span className="sdui-label">문서번호</span>
            <input className="sdui-input" value={docNumber} onChange={(e) => setDocNumber(e.target.value)} />
          </label>
        )}
        <label className="sdui-field" style={{ minWidth: 160 }}>
          <span className="sdui-label">대상 버전 (선택)</span>
          <select className="sdui-input" value={targetVersionId} onChange={(e) => setTargetVersionId(e.target.value)}>
            <option value="">없음</option>
            {versions.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </label>
        <button className="btn primary" style={{ width: "auto" }} disabled={!title.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "생성"}
        </button>
      </div>
    </div>
  );
}
